/**
 * Text on a Path — colour swatches and hex parsing (WMPCM-9522, Story 2).
 *
 * There is no colour picker anywhere else in this frontend to reuse: the Type panel has no text
 * colour control at all, and the Object panel's `colors` state
 * (`object-panel/hooks/use-appearance-state.ts`) is dead (set, never rendered).
 * `TextOnPathConfig.colorHex` is a plain CSS colour string handed straight to the SVG `fill`
 * attribute, so this module only has to produce and validate `#rrggbb`.
 *
 * Parsing lives here rather than inline in the toolbar so the hex field's accepted forms are
 * testable without mounting a popover.
 */

/** Colour a new text-on-path starts at. */
export const TEXT_ON_PATH_DEFAULT_COLOR = '#000000'

/**
 * Swatch grid, laid out as two rows of eight: a neutral ramp then a hue ramp.
 *
 * Values are Tailwind's own `neutral`/600-weight palette entries, which is what the rest of the
 * editor chrome is built from — not an independent brand palette. AC-6's screenshots do not
 * specify a palette, so this is a starting set to be replaced if design supplies one.
 */
export const TEXT_ON_PATH_COLOR_SWATCHES = [
  // Neutrals
  '#000000',
  '#262626',
  '#525252',
  '#737373',
  '#A3A3A3',
  '#D4D4D4',
  '#F5F5F5',
  '#FFFFFF',
  // Hues
  '#DC2626',
  '#EA580C',
  '#CA8A04',
  '#16A34A',
  '#0891B2',
  '#2563EB',
  '#7C3AED',
  '#DB2777',
] as const

/** Number of swatches per row in the grid. Kept beside the list so the two can't drift. */
export const TEXT_ON_PATH_COLOR_SWATCHES_PER_ROW = 8

const SHORT_HEX = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i
const LONG_HEX = /^#?([0-9a-f]{6})$/i

/**
 * Canonicalises a user-typed hex colour to `#rrggbb`, or returns `null` when it isn't one.
 *
 * Accepts the leading `#` optionally, either the 3- or 6-digit form, and any case — all three are
 * things a user pastes. Returns uppercase so callers can compare colours by string without
 * remembering to normalise first.
 */
export function normalizeHexColor(input: string): string | null {
  const trimmed = input.trim()
  const short = SHORT_HEX.exec(trimmed)

  if (short) {
    const [, r, g, b] = short

    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase()
  }

  const long = LONG_HEX.exec(trimmed)

  return long ? `#${long[1]}`.toUpperCase() : null
}
